import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  AudioLines,
  BookOpen,
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  CircleQuestionMark,
  History,
  MessageSquare,
  Mic,
  MicOff,
  Minus,
  Moon,
  PhoneOff,
  Search,
  Quote,
  Settings2,
  StickyNote,
  Trash2,
  UserCheck,
  UserPlus,
  X,
  Pencil,
  Loader2,
} from 'lucide-react';

import { cn } from '../../../../lib/utils';
import { NURTURE_REASONS, DROP_REASONS } from '../../../../lib/leadReasons';
import { transferMyParty } from '../../../../lib/ringcentral/callControl';
import {
  useHotSeatAvailability,
  useMarkSignedOnCall,
  usePrepareHotSeatTransfer,
  useRecordHotSeat,
  useReassignRun,
  useSaveLeadEmail,
  useSaveLeadWorkNotes,
  useSetRunOutcome,
} from '../../../../services/onboardingBoard';
import { Picker } from '../../clientOnboarding/ClientOnboardingSlideOver';
import { useDeleteLead, useLeadTranscripts } from '../../../../services/leads';
import LeadTranscriptsCard from '../LeadTranscriptsCard';
import HotSeatTransferCard from './HotSeatTransferCard';
import { sourceIdentity } from '../boardUtils';
import SlotPickerOverlay from '../../scheduling/SlotPickerOverlay';
import MeetingActions from '../../scheduling/MeetingActions';
import { reviewWindowLabel, reviewWindowOf } from '../../scheduling/reviewWindow';
import NudgeComposer from '../potentialClients/NudgeComposer';
import JourneyTimeline from '../potentialClients/JourneyTimeline';
import { VA_GOV_LABEL, vaGovAnswerOf } from '../VaGovCheck';
import { isOwnedBy, NotYoursNote, VIEW_ONLY_TITLE } from '../ownership';
import { toldUsRows, typedNoteOf } from '../../../../lib/leadFacts';
import { SignupBody, signupStepNumber } from '../signup/SignupFlyover';
import {
  AboutCard,
  CallButton,
  ManageCard,
  ManageLabel,
  FolderTabs,
  HeaderChip,
  NotesEditor,
  PersonGroup,
  PersonRow,
  RAIL_PAD,
  SECTION_TONE,
  SectionHead,
  StepCrumb,
  TabPage,
  TextButton,
  TILE,
  WorkspaceFrame,
} from '../../workspace/WorkspaceKit';

// The call workspace (Nic 2026-09-23): the moment contact is made, the small
// call card opens into this full-screen workspace. Three zones (Nic
// 2026-10-08 overhaul, for laptop screens):
//   the header: who (name, where they are, source / VA.gov / rep chips) and
//     the call controls beside close (never interrupted, never hidden);
//   the body: a rail (about them, notes, history) and the work, one row of
//     tabs: Objectives (click to expand) | If they ask | Sign up;
//   the footer: the Next step (Book follow-up, Not interested).
// After hang-up the workspace stays until the X closes it.
// Every call that made contact ends with a next step before the X shows
// (Nic 2026-10-06): signed up, a follow-up booked within the next 2 days (tomorrow or the day after, 2026-10-27), or
// Nurture / Drop. Can't book it = Nurture, and a nurtured lead who reaches
// back out is flagged for the rep.

const formatPhone = (p) => {
  const d = String(p || '').replace(/\D/g, '');
  if (d.length === 10)
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d.startsWith('1')) {
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  return p || '';
};
const mmss = (ms) => {
  const s = Math.max(0, Math.floor((ms || 0) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const pad = (n) => String(n).padStart(2, '0');
  return h ? `${h}:${pad(m)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`;
};
const initialsOf = (first, last) =>
  `${(first || '?')[0] || ''}${(last || '')[0] || ''}`.toUpperCase();

// ---- The journey (the same four columns the Potential Clients board uses) --

const JOURNEY = [
  { key: 'talking', label: 'Talking' },
  { key: 'meeting', label: 'Meeting' },
  { key: 'signup', label: 'Signing up' },
  { key: 'signed', label: 'Signed' },
];
const journeyIndex = ({ signup, run, detectedBooking }) => {
  if (signup.signed) return 3;
  if (signup.current !== 'link' || signup.done.link) return 2;
  const stage = run?.pipelineStage;
  if (['signup_link_sent', 'account_made', 'agreement_out'].includes(stage))
    return 2;
  if (
    detectedBooking ||
    ['meeting_link_sent', 'meeting_booked', 'missed_meeting'].includes(stage)
  ) {
    return 1;
  }
  return 0;
};

// Where they are, as one slim line beside the name (Nic 2026-10-08).
const JourneyCrumb = ({ index }) => <StepCrumb steps={JOURNEY} index={index} />;

// ---- Left: the person ------------------------------------------------------

// The rail (Nic 2026-10-08 overhaul; since 2026-10-21 the Onboarding
// Workspace's own About them card): About them (reference), What they told
// us, History (folded). Notes are their own tab, like Onboarding's. The
// pieces live in the Workspace kit (../../workspace/WorkspaceKit).

const HistoryGroup = ({ leadId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={RAIL_PAD}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="block w-full rounded-md text-left outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60"
      >
        <SectionHead
          icon={History}
          tone="violet"
          right={
            <ChevronDown
              size={15}
              className={cn(
                'text-slate-400 transition-transform',
                open ? 'rotate-180' : '',
              )}
            />
          }
        >
          History
        </SectionHead>
      </button>
      {open ? (
        <div className="mt-[10px]">
          <JourneyTimeline leadId={leadId} bare compact />
        </div>
      ) : null}
    </div>
  );
};

// The email row (Nic 2026-10-21): many entry points arrive without one, so
// the rep adds or corrects it here. One person, one email: the server
// refuses an address anyone else has (+tag and Gmail-dot copies too) and
// says where, and refuses a domain that can't receive mail.
export const EmailRow = ({ lead, canEdit, lit, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [problem, setProblem] = useState('');
  const save = useSaveLeadEmail();
  const email = lead.leadEmail || '';

  const submit = async () => {
    const value = draft.trim();
    if (!value || save.isLoading) return;
    if (value.toLowerCase() === email.toLowerCase()) {
      setEditing(false);
      return;
    }
    setProblem('');
    try {
      const res = await save.mutateAsync({ leadId: lead.leadId, email: value });
      onSaved?.(res?.leadEmail ?? value);
      setEditing(false);
      toast.success(email ? 'Email updated.' : 'Email added.');
    } catch (err) {
      setProblem(err?.response?.data?.msg || "That email couldn't be saved.");
    }
  };

  if (editing) {
    return (
      <div className="-mx-[8px] rounded-md px-[8px] py-[2px]">
        <div className="flex items-center justify-between gap-[8px]">
          <span className="shrink-0 text-xs font-normal text-slate-400 dark:text-slate-500">Email</span>
          <span className="inline-flex min-w-0 items-center gap-1">
            <input
              type="email"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              aria-label="Email"
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setProblem('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
                if (e.key === 'Escape') {
                  // Cancel only the edit, never the Workspace behind it.
                  e.stopPropagation();
                  setEditing(false);
                }
              }}
              placeholder="name@example.com"
              className="h-7 w-48 min-w-0 rounded-md bg-white px-2 text-[13px] text-slate-700 outline-none ring-1 ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-sky-400 dark:!bg-white/5 dark:text-slate-200 dark:ring-white/10"
            />
            <button
              type="button"
              onClick={submit}
              disabled={!draft.trim() || save.isLoading}
              aria-label="Save email"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-emerald-600 outline-none hover:bg-emerald-50 focus:outline-none disabled:opacity-40 dark:text-emerald-400 dark:hover:!bg-emerald-500/10"
            >
              {save.isLoading ? <Loader2 size={14} className="animate-spin" /> : <Check size={15} />}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              aria-label="Cancel"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 outline-none hover:bg-slate-100 focus:outline-none dark:hover:!bg-white/10"
            >
              <X size={14} />
            </button>
          </span>
        </div>
        {problem ? (
          <div role="alert" className="mt-1 text-xs leading-snug text-rose-600 dark:text-rose-400">
            {problem}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <PersonRow label="Email" title={email || undefined} muted={!email} lit={lit}>
      {/* A long email shortens with "…"; the pencil always stays in view
          (Nic 2026-10-23: it was pushed out of the card). The full address
          is on hover. */}
      <span className="flex min-w-0 items-center justify-end gap-1.5">
        <span className="min-w-0 truncate">{email || 'None on file'}</span>
        {canEdit ? (
          <button
            type="button"
            onClick={() => {
              setDraft(email);
              setProblem('');
              setEditing(true);
            }}
            aria-label={email ? 'Edit email' : 'Add email'}
            title={email ? 'Edit email' : 'Add email'}
            className="shrink-0 not-italic text-slate-300 outline-none hover:text-slate-500 focus:outline-none dark:text-slate-600 dark:hover:text-slate-400"
          >
            <Pencil size={12} />
          </button>
        ) : null}
      </span>
    </PersonRow>
  );
};

// Source, referrer, VA.gov and the rep ride the header as chips; the rail
// keeps the rest.
// armedChannel: the send card armed in Sign up ('sms' | 'email' | null).
const PersonPanel = ({ lead, run, readOnly = false, armedChannel = null, onEmailSaved = null, emailLocked = false }) => {
  const snap = lead?.leadMeta?.veteranSnapshot || null;
  // What they told us as clean rows, like the slide-over (2026-10-14), plus
  // any note someone actually typed.
  const toldRows = toldUsRows({
    isVeteran: lead?.leadMeta?.isVeteran,
    rank: lead?.leadMeta?.rank,
    vaGovAccount: lead?.leadVaGovAccount,
  });
  const told = typedNoteOf({
    sourceSystem: lead?.leadSourceSystem || lead?.leadMeta?.sourceSystem,
    notes: lead?.leadMeta?.notes,
  });
  const facts = [
    [
      'Rating',
      snap?.currentVaPercentage != null ? `${snap.currentVaPercentage}%` : null,
    ],
    ['Branch', snap?.branch],
    ['Rank', snap?.rankPaygrade],
    [
      'Location',
      snap?.location?.city
        ? `${snap.location.city}, ${snap.location.state || ''}`
        : null,
    ],
    ['Open claim', snap?.openClaim],
  ].filter(([, v]) => v);
  return (
    <AboutCard>
        {/* About them: one list, a quiet label left, the value right. Info
            only, nothing to pick (Nic 2026-09-30). */}
        <PersonGroup>
          <PersonRow label="Phone" lit={armedChannel === 'sms' ? 'sky' : null}>
            {formatPhone(lead.leadPhone)}
          </PersonRow>
          <EmailRow
            lead={lead}
            // Once they have an account the email is their login (2026-10-21):
            // the server refuses edits to a converted lead, so no pencil.
            canEdit={!readOnly && !emailLocked}
            lit={armedChannel === 'email' ? 'violet' : null}
            onSaved={onEmailSaved}
          />
          {facts.map(([k, v]) => (
            <PersonRow key={k} label={k}>
              {v}
            </PersonRow>
          ))}
          {run?.contactMadeAt ? (
            <PersonRow label="Contact made">
              {new Date(run.contactMadeAt).toLocaleString([], {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </PersonRow>
          ) : null}
        </PersonGroup>

        {toldRows.length ? (
          <PersonGroup title="What they told us" icon={Quote} tone="blue">
            {toldRows.map(([k, v]) => (
              <PersonRow key={k} label={k}>
                {v}
              </PersonRow>
            ))}
          </PersonGroup>
        ) : null}
        {told ? (
          <div className={RAIL_PAD}>
            {toldRows.length ? null : (
              <div className="mb-[8px]">
                <SectionHead icon={Quote} tone="blue">
                  What they told us
                </SectionHead>
              </div>
            )}
            <p className="mb-0 whitespace-pre-wrap text-[13px] leading-relaxed text-slate-600 dark:text-slate-300">
              {told}
            </p>
          </div>
        ) : null}

        {/* Everything that happened, newest first (Nic 2026-10-06: moved in
            from the side panel, which the Workspace replaces). */}
        <HistoryGroup leadId={lead.leadId} />
    </AboutCard>
  );
};

// ---- Center: the talk track ------------------------------------------------

// Smarter "If they ask" search (Nic 2026-09-30: "cost" found nothing, but
// the rep wanted "That's too expensive" and "Why am I paying you?"). Each
// typed word also matches the other words of its topic below, and a word
// counts when it starts a word in the text ("expens" finds "expensive").
// Every typed word must land somewhere; hits in the question (or its
// Keywords, set in the Call guide) outrank hits in the answer. Plain words
// only: nothing leaves the browser.
const SEARCH_TOPICS = [
  ['cost', 'costs', 'price', 'pricing', 'expensive', 'afford', 'pay', 'paying',
    'paid', 'payment', 'fee', 'fees', 'charge', 'charging', 'money', 'bill',
    'billing', 'invoice', 'cheap', 'owe'],
  ['long', 'time', 'timeline', 'wait', 'weeks', 'months', 'fast', 'quick',
    'soon'],
  ['rating', 'ratings', 'percent', 'increase', 'raise', 'lower', 'decrease',
    'reduce', 'drop', 'lose'],
  ['vso', 'free', 'dav', 'vfw', 'legion', 'nonprofit'],
  ['allowed', 'legal', 'legit', 'law', 'accredited', 'accreditation',
    'charge', 'scam', 'trust'],
  ['spouse', 'wife', 'husband', 'family', 'partner'],
  ['think', 'later', 'decide', 'consider', 'sleep', 'review'],
  ['denied', 'denial', 'rejected', 'appeal'],
  ['va.gov', 'vagov', 'account', 'login', 'idme', 'id.me', 'password'],
  ['who', 'company', 'different', 'veritas'],
  ['va', 'government', 'affiliated', 'official'],
];
const wordsOf = (text) =>
  String(text || '')
    .toLowerCase()
    .split(/[^a-z0-9.%']+/)
    .map((w) => w.replace(/^'+|'+$/g, ''))
    .filter(Boolean);
// The typed word plus every word of each topic it belongs to (by prefix, so
// a half-typed word still expands).
const expandWord = (w) => {
  const out = new Set([w]);
  if (w.length < 3) return out;
  SEARCH_TOPICS.forEach((topic) => {
    if (topic.some((t) => t.startsWith(w) || (t.length > 3 && w.startsWith(t)))) {
      topic.forEach((t) => out.add(t));
    }
  });
  return out;
};
const hitsIn = (terms, words) =>
  words.some((word) => [...terms].some((t) => word.startsWith(t)));
const searchFaqs = (faqs, query) => {
  const typed = wordsOf(query);
  if (!typed.length) return faqs;
  const expanded = typed.map(expandWord);
  return faqs
    .map((f, i) => {
      // A manager's keywords (and filter tags) count as much as the
      // question itself.
      const qWords = wordsOf(`${f.q} ${f.keywords || ''} ${(f.tags || []).join(' ')}`);
      const aWords = wordsOf(f.a);
      let score = 0;
      for (const terms of expanded) {
        const inQ = hitsIn(terms, qWords);
        const inA = !inQ && hitsIn(terms, aWords);
        if (!inQ && !inA) return null;
        score += inQ ? 3 : 1;
      }
      return { f, score, i };
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score || a.i - b.i)
    .map((x) => x.f);
};

// Filter tags (Nic 2026-10-26): every tag in use, one spelling each, A-Z.
export const faqTagsOf = (faqs = []) =>
  [
    ...faqs
      .flatMap((f) => f.tags || [])
      // The first spelling wins.
      .reduce((m, t) => (m.has(t.toLowerCase()) ? m : m.set(t.toLowerCase(), t)), new Map())
      .values(),
  ].sort((a, b) => a.localeCompare(b));
// The answers shown: the picked tag first, then the search.
export const filterFaqs = (faqs = [], { q = '', tag = null } = {}) => {
  const tagged = tag ? faqs.filter((f) => (f.tags || []).some((t) => t.toLowerCase() === tag.toLowerCase())) : faqs;
  return q.trim() ? searchFaqs(tagged, q.trim().toLowerCase()) : tagged;
};

// sub = 'objectives' | 'ask', picked by the Workspace's one row of tabs
// (Nic 2026-10-08: the talk track had its own second row of tabs).
// A filter tag opens every answer under it; clearing the tag closes them all
// (Nic 2026-10-29). No tag opens nothing.
export const openFaqsFor = (faqs = [], { q = '', tag = null } = {}) =>
  new Set(tag ? filterFaqs(faqs, { q, tag }).map((f) => f.q) : []);

const GuidePanel = ({ guide, firstName, sub = 'objectives' }) => {
  const sections = guide?.sections || [];
  const faqs = guide?.faqs || [];
  const [query, setQuery] = useState('');
  // The questions whose answers are open. A tag opens all of its answers at
  // once; clicking a question toggles just that one.
  const [openFaqs, setOpenFaqs] = useState(() => new Set());
  // Objectives open one at a time by click (Nic 2026-10-08: the full text
  // of every one at once showed three on a laptop). Expand all opens them.
  const [openObj, setOpenObj] = useState(() => new Set());
  const allOpen = sections.length > 0 && openObj.size === sections.length;
  const toggleObj = (i) =>
    setOpenObj((s) => {
      const next = new Set(s);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  const q = query.trim().toLowerCase();
  // One filter tag at a time; clicking it again clears it.
  const [tag, setTag] = useState(null);
  const tags = faqTagsOf(faqs);
  const shownFaqs = filterFaqs(faqs, { q, tag });
  const fill = (text) =>
    String(text || '').replace(
      /\{first name\}|\[first name\]/gi,
      firstName || 'there',
    );

  return (
    <div>
      {sub === 'objectives' ? (
        <section>
          {sections.length === 0 ? (
            <div className={cn(TILE, 'px-[16px] py-[14px] text-sm text-slate-400')}>
              No talk track yet. A manager can add one in board settings, under
              Call guide.
            </div>
          ) : (
            <>
              <div className="mb-[8px] flex items-center justify-between">
                <span className="text-xs text-slate-400 dark:text-slate-500">
                  Click one to read it.
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setOpenObj(allOpen ? new Set() : new Set(sections.map((_, i) => i)))
                  }
                  className="rounded-md px-[8px] py-[2px] text-xs font-semibold text-sky-600 outline-none hover:bg-sky-50 focus:outline-none dark:text-sky-300 dark:hover:!bg-sky-500/10"
                >
                  {allOpen ? 'Collapse all' : 'Expand all'}
                </button>
              </div>
              <ol
                className={cn(
                  TILE,
                  'mb-0 list-none divide-y divide-slate-200/60 pl-0 dark:divide-white/5',
                )}
              >
                {sections.map((sec, i) => {
                  const open = openObj.has(i);
                  return (
                    <li key={`${sec.title}-${i}`}>
                      <button
                        type="button"
                        aria-expanded={open}
                        onClick={() => toggleObj(i)}
                        className="flex w-full items-center gap-[12px] px-[14px] py-[10px] text-left outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-400/60"
                      >
                        {/* Tinted numbers (Nic 2026-09-30): grey on grey
                            gave the eye no path down the list. */}
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-100 text-[11px] font-bold text-sky-700 dark:!bg-sky-500/20 dark:text-sky-200">
                          {i + 1}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
                          {sec.title}
                        </span>
                        <ChevronDown
                          size={15}
                          className={cn(
                            'shrink-0 text-slate-400 transition-transform',
                            open ? 'rotate-180' : '',
                          )}
                        />
                      </button>
                      {open ? (
                        <p className="mb-0 whitespace-pre-wrap pb-[12px] pl-[50px] pr-[16px] text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                          {fill(sec.body)}
                        </p>
                      ) : null}
                    </li>
                  );
                })}
              </ol>
            </>
          )}
        </section>
      ) : (
        <section>
          <div className="mb-[10px]">
            <label className="relative block w-full">
              <Search
                size={14}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setQuery('');
                }}
                placeholder="Search answers"
                className="w-full rounded-lg bg-white py-2 pl-8 pr-8 text-sm text-slate-700 outline-none ring-1 ring-slate-200 focus:outline-none focus:ring-slate-300 dark:!bg-white/5 dark:text-slate-200 dark:ring-white/10"
              />
              {query ? (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                  title="Clear"
                  className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 outline-none hover:bg-slate-100 hover:text-slate-600 focus:outline-none dark:hover:!bg-white/10 dark:hover:text-slate-200"
                >
                  <X size={12} />
                </button>
              ) : null}
            </label>
            {tags.length ? (
              <div className="mt-[8px] flex flex-wrap gap-[6px]" aria-label="Filter by tag">
                {tags.map((t) => {
                  const on = tag && tag.toLowerCase() === t.toLowerCase();
                  return (
                    <button
                      key={t}
                      type="button"
                      aria-pressed={!!on}
                      onClick={() => {
                        const next = on ? null : t;
                        setTag(next);
                        setOpenFaqs(openFaqsFor(faqs, { q, tag: next }));
                      }}
                      className={cn(
                        'rounded-full px-[10px] py-[3px] text-xs font-semibold outline-none ring-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60',
                        on
                          ? 'bg-slate-900 text-white ring-slate-900 dark:!bg-white dark:!text-slate-900 dark:ring-white'
                          : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50 dark:!bg-white/5 dark:text-slate-300 dark:ring-white/10 dark:hover:!bg-white/10',
                      )}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
          <div
            className={cn(
              TILE,
              'divide-y divide-slate-200/60 dark:divide-white/5',
            )}
          >
            {shownFaqs.length === 0 ? (
              <div className="px-[16px] py-[14px] text-sm text-slate-400">
                {!faqs.length ? 'No answers yet.' : tag ? `Nothing tagged ${tag} matches that.` : 'Nothing matches that.'}
              </div>
            ) : null}
            {shownFaqs.map((f) => {
              const open = openFaqs.has(f.q) || !!q;
              return (
                <div key={f.q}>
                  <button
                    type="button"
                    onClick={() =>
                      setOpenFaqs((s) => {
                        const next = new Set(s);
                        if (next.has(f.q)) next.delete(f.q);
                        else next.add(f.q);
                        return next;
                      })
                    }
                    className="flex w-full items-center gap-2 px-[14px] py-[10px] text-left text-sm font-semibold text-slate-700 outline-none focus:outline-none dark:text-slate-200 whitespace-nowrap"
                  >
                    <ChevronRight
                      size={14}
                      className={cn(
                        'shrink-0 text-slate-400 transition-transform',
                        open ? 'rotate-90' : '',
                      )}
                    />
                    {f.q}
                  </button>
                  {open ? (
                    // The answer reads as secondary (Nic 2026-09-30): tucked
                    // under the question text, on a tint with a rule in the tab's indigo (2026-10-21).
                    // Padding, not margin, keeps the gap inside the tile: a
                    // bottom margin on the last answer collapsed through the
                    // tile and the answer sat on its edge (Nic 2026-10-29).
                    <div className="pb-[12px]">
                      <div className="ml-[36px] mr-[16px] rounded-r-lg border-l-2 border-indigo-400 bg-slate-50 px-3.5 py-2.5 dark:border-indigo-400/60 dark:!bg-white/[0.04]">
                        <p className="mb-0 whitespace-pre-wrap text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                          {fill(f.a)}
                        </p>
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

// ---- Notes: their own tab (Nic 2026-10-21, like the Onboarding Workspace) --
// Sales' notes on the lead. Onboarding reads them, locked, after they sign.

// The open call keeps its own copy of the lead, so a save writes back into it
// (onSaved): leaving the tab and coming back never shows the old text (branch
// QA 2026-10-21). The Workspace also keeps this tab mounted while another is
// open, so unsaved typing survives a tab switch.
const NotesTab = ({ lead, readOnly = false, onSaved = null }) => {
  const queryClient = useQueryClient();
  const save = useSaveLeadWorkNotes(() => {
    queryClient.invalidateQueries(['onboarding-board']);
  });
  const savedAt = lead.leadWorkNotesUpdatedAt
    ? new Date(lead.leadWorkNotesUpdatedAt).toLocaleDateString([], { month: 'short', day: 'numeric' })
    : null;
  return (
    <NotesEditor
      key={lead.leadId}
      title="Sales notes"
      sub={
        savedAt
          ? `Last saved ${savedAt}`
          : readOnly
            ? null
            : 'Onboarding sees these after they sign.'
      }
      initial={lead.leadWorkNotes || ''}
      readOnly={readOnly}
      saving={save.isLoading}
      placeholder="What you learned: conditions, goals, who else is involved, when to reach them..."
      onSave={async (text) => {
        try {
          await save.mutateAsync({ leadId: lead.leadId, notes: text });
          onSaved?.({ leadWorkNotes: text, leadWorkNotesUpdatedAt: new Date().toISOString() });
        } catch (e) {
          toast.error(e?.response?.data?.msg || "Notes couldn't be saved.");
          throw e;
        }
      }}
    />
  );
};

// Not interested: pick Nurture or Drop (the words the board uses), then one
// reason from soft rows. The chosen row is a solid fill with white text (the
// board's selected look); the list scrolls inside the popover. Selection
// controls never fade their color (Nic 2026-09-30): a fading row read as
// the old pick flashing back.
// Book follow-up asks WHY first (Nic 2026-09-30), then opens the picker on
// times within the next 2 days (tomorrow or the day after, 2026-10-27): never leave it to the veteran (Jorge's First
// Touch doc). "Other" takes a few words of its own.
const OTHER_REASON = 'Other';
const REVIEW_REASONS = [
  'Wants to think it over',
  'Talking it over with spouse or family',
  'Wants to read the agreement first',
  OTHER_REASON,
];
// The window itself: the next two work days, every slot (Nic 2026-10-27,
// scheduling/reviewWindow.js).
// The Nurture reason for a follow-up that couldn't be booked on the call
// (in NURTURE_REASONS on both sides).
const COULDNT_BOOK = "Couldn't book a follow-up";
// What happens without a follow-up, said the same way everywhere.
const NO_BOOK_RULE =
  "If you can't book it, they go to Nurture, and we'll alert you if they ever reach back out.";

// Manage tab (Nic 2026-10-15): reasons are a dropdown to save space, the
// same picker as "Pick a rep", so all three tiles read alike.
const ReasonPicker = ({ reasons, value, onChange, label = 'Reason' }) => (
  <div className="[&>*:last-child]:w-full">
    <ManageLabel>{label}</ManageLabel>
    <Picker
      value={value}
      placeholder="Pick a reason"
      options={reasons.map((r) => ({ value: r, label: r }))}
      onPick={onChange}
    />
  </div>
);

// "Other" takes a few words of its own (Nic 2026-09-30), capped short so it
// stays a label, never a call summary (the server caps it at 40 too).
const OTHER_MAX = 40;
const OtherInput = ({ value, onChange, onEnter }) => (
  <input
    // eslint-disable-next-line jsx-a11y/no-autofocus
    autoFocus
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onKeyDown={(e) => {
      if (e.key === 'Enter') onEnter?.();
    }}
    maxLength={OTHER_MAX}
    placeholder="What's the reason?"
    className="mt-2 w-full rounded-lg bg-white px-3 py-2 text-[13px] text-slate-700 outline-none ring-1 ring-slate-200 focus:outline-none focus:ring-slate-300 dark:!bg-white/5 dark:text-slate-100 dark:ring-white/10"
  />
);

// ---- Hot seat at signing (Nic 2026-09-28) ------------------------------------
// The moment they sign, the rep hands the client to an Onboarding Specialist.
// Did the agreement land while a call was live? Judged once, the moment
// the signature shows up. A hangup a moment before still counts (the 5
// second poll can trail the call by a little).
const SIGNED_ON_CALL_GRACE_MS = 2 * 60 * 1000;
const useSignedOnCall = (call, clientUserId, liveCall = null) => {
  const [onCall, setOnCall] = useState(null);
  const mark = useMarkSignedOnCall();
  const judged = useRef(false);
  useEffect(() => {
    if (!clientUserId || judged.current) return;
    judged.current = true;
    const justEnded =
      !!call.callEndedAt && Date.now() - call.callEndedAt < SIGNED_ON_CALL_GRACE_MS;
    // A hot seat already recorded (the top bar transfer) proves the call.
    // So does the call on another device (their cell, the RingCentral app;
    // the server sees it: review 2026-11-04).
    const on = !!call.callLive || justEnded || !!call.hotSeatDone || !!liveCall;
    setOnCall(on);
    if (on) mark.mutate({ userId: clientUserId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientUserId]);
  return onCall;
};

const barBtn =
  'inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-xl px-4 text-sm font-semibold outline-none transition-[background-color,color,box-shadow] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 disabled:cursor-not-allowed disabled:opacity-40';
const quietBtn = `${barBtn} bg-slate-100 text-slate-700 hover:bg-slate-200 dark:!bg-white/10 dark:text-slate-200 dark:hover:!bg-white/15`;

// ---- The workspace -----------------------------------------------------------

const CallWorkspace = ({
  call,
  muted,
  elapsedMs,
  busy,
  actions,
  signup,
  detectedBooking,
  calendlyUrl,
  targets = [],
  // Their assigned onboarding specialist, once signed (the only hot seat).
  specialist = null,
  // VA.gov ready when they signed: false means no hot seat at all.
  vaGovReady = true,
  // The client tapped "I've done it" since signing: the hot seat opened.
  vaGovJustDone = false,
  // The rep's live call with this client on ANY device, as the server sees
  // it (Nic 2026-11-03, hot transfer from any device): { sessionId,
  // partyId, since }. Null when they are not on the phone with them.
  liveCall = null,
}) => {
  const queryClient = useQueryClient();
  const { lead, run, boardContext } = call;
  // Ownership rule (Nic 2026-09-29; 2026-10-23 one rule everywhere): only
  // the owner works a person. Anyone else (the manager, an admin, a viewer)
  // opens it read only: every action shows, greyed out (the server 403s them
  // too). The manager keeps Reassign, to move it to himself first.
  const viewer = boardContext?.viewer || null;
  const viewOnly = viewer?.canAct === false;
  const isManager = !!viewer?.isManager;
  const notMine =
    !!call.readOnly ||
    viewOnly ||
    (!!run?.ownerUserId && !isOwnedBy(run.ownerUserId, boardContext?.viewerUserId));
  const greyTitle = notMine ? VIEW_ONLY_TITLE : undefined;
  const mode =
    !notMine && call.workspaceMode === 'signup' ? 'signup' : 'guide';
  const live = !!call.callLive;
  // The talk track's two views, in the one row of tabs with Sign up.
  const [guideSub, setGuideSub] = useState(call.openTab || 'objectives');
  // Opened on a tab (Notes): used once, so a minimize and restore comes back
  // to Objectives rather than that tab again.
  useEffect(() => {
    if (call.openTab) actions.setWorkspaceMode('guide');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Which Sign up send card is armed, so the rail lights that row.
  const [armedChannel, setArmedChannel] = useState(null);
  const [bookPicker, setBookPicker] = useState(false);
  // The meeting being moved (Reschedule), or null.
  const [moving, setMoving] = useState(null);
  const [texting, setTexting] = useState(false);
  const [confirmCall, setConfirmCall] = useState(false);
  const [nurtureReason, setNurtureReason] = useState('');
  const [dropReason, setDropReason] = useState('');
  const [parked, setParked] = useState(null); // nurture | drop, after the fact
  // "Wants to review": the reason picked, and whether the follow-up it
  // requires got booked. Until then the rep cannot close the call screen.
  const [reviewReason, setReviewReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [nurtureOther, setNurtureOther] = useState('');
  const [dropOther, setDropOther] = useState('');
  const [reviewPending, setReviewPending] = useState(false);
  const [reviewBooked, setReviewBooked] = useState(false);
  const [bookWindow, setBookWindow] = useState(null);

  const setOutcome = useSetRunOutcome();
  const dropLead = useDeleteLead();
  const reassignRun = useReassignRun();
  const [toRepId, setToRepId] = useState('');
  // Which Manage row is open: 'book' | 'notInterested' | 'reassign' | null.
  const [manageOpen, setManageOpen] = useState(null);
  const toggleManage = (key) => setManageOpen((k) => (k === key ? null : key));
  const openManage = () => {
    setGuideSub('manage');
    actions.setWorkspaceMode('guide');
  };

  const signupStarted = signup.current !== 'link' || signup.done.link;
  // Their recorded calls (RingSense), the same card as the side panel's.
  const transcripts = useLeadTranscripts('/onboarding-board/leads', lead.leadId);
  const transcriptCount = transcripts.data?.transcripts?.length || 0;
  // The work area's tabs, each in its own color (Nic 2026-10-08).
  const workTabs = [
    {
      key: 'objectives',
      icon: BookOpen,
      label: 'Objectives',
      tone: 'sky',
      badge: boardContext?.callGuide?.sections?.length || null,
    },
    {
      key: 'ask',
      icon: MessageSquare,
      label: 'If they ask',
      tone: 'indigo',
      badge: boardContext?.callGuide?.faqs?.length || null,
    },
    // Transcripts, Notes, Manage, then Sign up last (Nic 2026-10-21, the
    // Onboarding Workspace's order with Sign up, the green one, at the end):
    // their recorded calls, and Notes out of the rail.
    {
      key: 'transcripts',
      icon: AudioLines,
      label: 'Transcripts',
      tone: 'rose',
      badge: transcriptCount || null,
    },
    {
      key: 'notes',
      icon: StickyNote,
      label: 'Notes',
      tone: 'amber',
      badge: String(lead.leadWorkNotes || '').trim() ? '1' : null,
    },
    // Manage (Nic 2026-10-14, like Client Onboarding): book a follow-up,
    // Not interested, or give them to another rep.
    {
      key: 'manage',
      icon: Settings2,
      label: 'Manage',
      tone: 'violet',
    },
    {
      key: 'signup',
      icon: signup.signed ? Check : UserPlus,
      label: signup.signed ? 'Signed up' : 'Sign up',
      tone: 'emerald',
      // Where the signup stands, so a started one is never lost.
      badge: signup.signed
        ? null
        : signupStarted
          ? `Step ${signupStepNumber(signup.current)} of 4`
          : null,
      disabled: notMine,
    },
  ];
  const activeTab = mode === 'signup' ? 'signup' : guideSub;
  const journey = journeyIndex({ signup, run, detectedBooking });
  // Opened straight from a follow-up Call, not dialed yet (or the dial
  // failed): the top bar offers Place call / Try again.
  const precall = !call.placed && !call.callLive;
  const nextStepSet =
    signup.signed ||
    signupStarted ||
    !!detectedBooking ||
    !!parked ||
    reviewBooked;
  // The lock is for the call that made contact (Nic 2026-10-06): opened from
  // a card (not dialed) or on a follow-up call, closing the picker must not
  // trap the rep in the Workspace.
  const mustBookReview =
    reviewPending && !reviewBooked && !detectedBooking && !call.followUp;
  // This call made contact (not a follow-up call that may never have
  // connected): it needs a next step before the X shows (Nic 2026-10-06).
  const requireOutcome =
    !notMine && !call.followUp && !!call.contactMade && !nextStepSet;

  // Signed: name the client so a Transfer at the top becomes a hot seat.
  const clientUserId = signup.signed ? signup.row?.userId || null : null;
  const signedOnCall = useSignedOnCall(call, clientUserId, liveCall);
  const { setHotSeatClient } = actions;
  useEffect(() => {
    if (clientUserId && setHotSeatClient) setHotSeatClient(clientUserId);
  }, [clientUserId, setHotSeatClient]);
  // Hot seat transfer on the signed screen (Nic 2026-10-23): Warm or Direct
  // (2026-10-29), to their specialist or, when that person isn't free, to
  // another specialist who is. RingCentral says if they picked up.
  // The call on another device (their cell, the RingCentral app): the server
  // sees it, the card shows, and Direct moves it from this browser with
  // the rep's own RingCentral sign-in (2026-11-04). A transfer made that
  // way is marked on the call like the browser's own (`hotSeatDone`), so
  // the lights stop polling and the signed line reads the same.
  const offDevice = !!liveCall && !call.callLive;
  const hotSeatShown =
    !notMine && !!clientUserId && (!!call.callLive || !!call.hotSeatDone || !!liveCall);
  // The lights, polled while the card is up (faster while a consult rings),
  // and the rep's own consult as the specialist's line reports it.
  const availabilityQ = useHotSeatAvailability(lead?.leadId, {
    enabled: hotSeatShown && !call.hotSeatDone && vaGovReady,
    consulting: !!call.consult && call.consult.state !== 'no_answer',
  });
  const specialists = availabilityQ.data?.specialists
    ? availabilityQ.data.specialists
    : specialist
      ? // Before the first poll: their name, with the extension the board knows.
        [{ userId: specialist.userId, name: specialist.name, assigned: true, availability: null, extension: targets.find((t) => t.userId === specialist.userId)?.extension || null }]
      : null;
  // The specialist's line answered or missed the consult: the server saw it.
  const serverConsult = availabilityQ.data?.consult || null;
  const { consultAnswered, consultMissed } = actions;
  useEffect(() => {
    const consult = call.consult;
    if (!consult || consult.state !== 'ringing' || !serverConsult) return;
    if (serverConsult.toUserId !== consult.target?.userId) return;
    if (new Date(serverConsult.at).getTime() < consult.since - 15000) return;
    if (serverConsult.state === 'answered') consultAnswered?.();
    else if (serverConsult.state === 'missed') consultMissed?.();
  }, [call.consult, serverConsult, consultAnswered, consultMissed]);
  const [hotSeatSending, setHotSeatSending] = useState(null);
  // Bring the call here (2026-11-04): the switch runs in the provider; this
  // only shows the button busy until the call is live here or it gave up.
  const [bringing, setBringing] = useState(false);
  useEffect(() => {
    if (call.callLive) setBringing(false);
  }, [call.callLive]);
  const bringHere = async () => {
    if (!liveCall?.sessionId || !actions.bringCallHere) return;
    setBringing(true);
    try {
      await actions.bringCallHere({ sessionId: liveCall.sessionId, startedAt: liveCall.startedAt || null, direction: liveCall.direction || null });
    } finally {
      setBringing(false);
    }
  };
  const prepareHotSeat = usePrepareHotSeatTransfer();
  const recordOffDeviceHotSeat = useRecordHotSeat({ onError: () => {} });
  const sendHotSeat = async (target, { mode = 'direct', via = null, redirectFrom = null, reason = null } = {}) => {
    setHotSeatSending(target.userId);
    const meta = redirectFrom ? { redirectFrom, redirectFromName: specialist?.name || null, reason } : {};
    try {
      if (via === 'server' || offDevice) {
        // The call is on their phone (2026-11-04): the server checks and
        // names the session, the rep's party and the target; this browser
        // moves the call with the rep's OWN RingCentral sign-in (only a
        // call's owner may control it); then the record is posted. Direct
        // only.
        let prep;
        try {
          prep = await prepareHotSeat.mutateAsync({
            userId: clientUserId,
            toUserId: target.userId,
            ...(redirectFrom ? { redirectFrom, reason } : {}),
          });
          await transferMyParty({ telephonySessionId: prep.sessionId, partyId: prep.partyId, ...prep.target });
        } catch (e) {
          toast.error(e?.response?.data?.msg || e?.message || 'Could not transfer the call. It stays on your phone.');
          return;
        }
        // The call has moved. The record is its own step (review
        // 2026-11-04): if it fails the rep is still told the truth, that
        // the client is with the specialist, never that it stayed.
        const sentName = prep.ownerName || target.name;
        const done = (toName) => actions.markHotSeatDone({ toName, insteadOf: redirectFrom ? specialist?.name || null : null });
        try {
          const rec = await recordOffDeviceHotSeat.mutateAsync({
            userId: clientUserId,
            toUserId: target.userId,
            mode: 'direct',
            engine: 'rc-api',
            transferSessionId: prep.sessionId,
            ...(redirectFrom ? { redirectFrom, reason } : {}),
          });
          done(rec?.ownerName || sentName);
          toast.success(`Sent to ${rec?.ownerName || sentName}.`);
        } catch (e) {
          done(sentName);
          toast.warn(`Sent to ${sentName}, but it could not be recorded. Let them know they have ${lead?.leadFirstName || 'the client'}.`);
        }
      } else if (mode === 'warm') await actions.beginWarmTransfer(target, meta);
      else await actions.beginBlindTransfer(target, meta);
    } finally {
      setHotSeatSending(null);
    }
  };
  const hotSeatCard = hotSeatShown ? (
    <HotSeatTransferCard
      specialist={specialist}
      specialists={specialists}
      vaGovReady={vaGovReady}
      vaGovJustDone={vaGovJustDone}
      clientFirstName={lead?.leadFirstName || null}
      consult={call.consult || null}
      onSend={sendHotSeat}
      onConsultAnswered={actions.consultAnswered}
      onCompleteConsult={actions.completeWarmTransfer}
      onCancelConsult={actions.cancelWarmTransfer}
      sending={!!hotSeatSending}
      sentTo={call.hotSeatDone?.toName || null}
      offDevice={offDevice}
      onBringHere={offDevice && actions.bringCallHere ? bringHere : null}
      bringing={bringing}
    />
  ) : null;

  // Other needs its words before Nurture or Drop can go.
  const nurtureReady =
    !!nurtureReason && (nurtureReason !== OTHER_REASON || !!nurtureOther.trim());
  const dropReady =
    !!dropReason && (dropReason !== OTHER_REASON || !!dropOther.trim());
  const nurtureWith = async (reason, detail = null) => {
    if (!run?.cadenceRunId) return;
    try {
      await setOutcome.mutateAsync({
        cadenceRunId: run.cadenceRunId,
        outcome: 'nurture',
        reason,
        ...(detail ? { reasonDetail: detail } : {}),
      });
      queryClient.invalidateQueries(['onboarding-board']);
      toast.success("Moved to Nurture. We'll alert you if they reach back out.");
      if (!live) actions.closeCenter();
      else setParked('nurture');
    } catch (e) {
      toast.error(e?.response?.data?.msg || "That didn't go through.");
    }
  };
  const doNurture = () => {
    if (!nurtureReady) return;
    nurtureWith(
      nurtureReason,
      nurtureReason === OTHER_REASON ? nurtureOther.trim() : null,
    );
  };
  const reviewText =
    reviewReason === OTHER_REASON ? otherReason.trim() : reviewReason;
  const doReview = () => {
    if (!reviewText) return;
    setReviewReason(reviewText);
    setReviewPending(true);
    const { fromMs, toMs } = reviewWindowOf();
    setBookWindow({ fromMs, toMs, label: reviewWindowLabel(reviewText) });
    setBookPicker(true);
  };
  const doDrop = async () => {
    if (!dropReady) return;
    try {
      await dropLead.mutateAsync({
        leadId: lead.leadId,
        reason: dropReason,
        ...(dropReason === OTHER_REASON
          ? { reasonDetail: dropOther.trim() }
          : {}),
      });
      queryClient.invalidateQueries(['onboarding-board']);
      toast.success('Lead dropped.');
      if (!live) actions.closeCenter();
      else setParked('drop');
    } catch (e) {
      toast.error(e?.response?.data?.msg || "That didn't go through.");
    }
  };

  const name = `${lead.leadFirstName || ''} ${lead.leadLastName || ''}`.trim();
  const src = sourceIdentity(lead);

  // ---- Manage tab (Nic 2026-10-14, like Client Onboarding's) ----
  // Book a follow-up, Not interested, or give them to another rep; the
  // footer's "Client not ready?" bar just opens it.
  const repOptions = (boardContext?.salesReps || [])
    .filter((r) => r.userId !== run?.ownerUserId)
    .map((r) => ({ value: r.userId, label: r.name }));
  const doReassign = async () => {
    if (!toRepId || !run?.cadenceRunId) return;
    const to = repOptions.find((o) => o.value === toRepId)?.label || 'them';
    try {
      await reassignRun.mutateAsync({
        cadenceRunId: run.cadenceRunId,
        toUserId: toRepId,
      });
      queryClient.invalidateQueries(['onboarding-board']);
      toast.success(`Moved to ${to}.`);
      actions.closeCenter();
    } catch (e) {
      toast.error(e?.response?.data?.msg || "That didn't go through.");
    }
  };
  const managePage = signup.signed ? (
    <p className="mb-0 text-sm text-slate-500 dark:text-slate-400">
      {lead.leadFirstName || 'They'} signed up, so there is nothing to manage
      here.
    </p>
  ) : (
    // Rows, one under another (Nic 2026-10-15); each opens its form. Nurture
    // and Drop are their own rows (no "Not interested" umbrella).
    <div className="space-y-2">
      <ManageCard
        icon={Calendar}
        tone="sky"
        title="Book a follow-up"
        open={manageOpen === 'book'}
        dimmed={!!manageOpen && manageOpen !== 'book'}
        onToggle={() => toggleManage('book')}
        blurb="Not signing today? Book within the next 2 days (tomorrow or the day after, 2026-10-27) while they're on the line."
        footer={
          detectedBooking ? null : (
            <div>
              <button
                type="button"
                disabled={notMine || !reviewText || busy}
              title={greyTitle}
                onClick={doReview}
                className="w-full whitespace-nowrap rounded-xl bg-sky-600 py-2.5 text-sm font-semibold text-white outline-none transition-[background-color] hover:bg-sky-500 focus:outline-none disabled:opacity-40"
              >
                Pick a time
              </button>
              <p className="mb-0 mt-2 text-xs leading-snug text-slate-500 dark:text-slate-400">
                {NO_BOOK_RULE}
              </p>
            </div>
          )
        }
      >
        {detectedBooking ? (
          // In-app meetings (2026-10-25): when, phone or video, and
          // Reschedule / Cancel / Missed / Join, owner only. An old Calendly
          // meeting reads the same, with no buttons (Calendly owns it).
          <MeetingActions
            meeting={detectedBooking}
            mount="board"
            leadId={lead.leadId}
            canAct={!notMine}
            onReschedule={setMoving}
          />
        ) : (
          <div>
            <ReasonPicker
              label="Why not today?"
              reasons={REVIEW_REASONS}
              value={reviewReason}
              onChange={setReviewReason}
            />
            {reviewReason === OTHER_REASON ? (
              <OtherInput
                value={otherReason}
                onChange={setOtherReason}
                onEnter={doReview}
              />
            ) : null}
          </div>
        )}
      </ManageCard>

      <ManageCard
        icon={Moon}
        tone="violet"
        title="Nurture"
        open={manageOpen === 'nurture'}
        dimmed={!!manageOpen && manageOpen !== 'nurture'}
        onToggle={() => toggleManage('nurture')}
        blurb="Not now, maybe later. We'll alert you if they ever reach back out."
        footer={
          <button
            type="button"
            disabled={notMine || !nurtureReady || setOutcome.isLoading}
            title={greyTitle}
            onClick={doNurture}
            className="w-full whitespace-nowrap rounded-xl bg-violet-600 py-2.5 text-sm font-semibold text-white outline-none transition-[background-color] hover:bg-violet-500 focus:outline-none disabled:opacity-40"
          >
            Move to Nurture
          </button>
        }
      >
        <div>
          <ReasonPicker
            reasons={NURTURE_REASONS}
            value={nurtureReason}
            onChange={setNurtureReason}
          />
          {nurtureReason === OTHER_REASON ? (
            <OtherInput
              value={nurtureOther}
              onChange={setNurtureOther}
              onEnter={doNurture}
            />
          ) : null}
        </div>
      </ManageCard>

      <ManageCard
        icon={Trash2}
        tone="rose"
        title="Drop"
        open={manageOpen === 'drop'}
        dimmed={!!manageOpen && manageOpen !== 'drop'}
        onToggle={() => toggleManage('drop')}
        blurb="Not a fit or asked us to stop. Closes them out."
        footer={
          <button
            type="button"
            disabled={notMine || !dropReady || dropLead.isLoading}
            title={greyTitle}
            onClick={doDrop}
            className="w-full whitespace-nowrap rounded-xl bg-rose-600 py-2.5 text-sm font-semibold text-white outline-none transition-[background-color] hover:bg-rose-500 focus:outline-none disabled:opacity-40"
          >
            Drop the lead
          </button>
        }
      >
        <div>
          <ReasonPicker
            reasons={DROP_REASONS}
            value={dropReason}
            onChange={setDropReason}
          />
          {dropReason === OTHER_REASON ? (
            <OtherInput
              value={dropOther}
              onChange={setDropOther}
              onEnter={doDrop}
            />
          ) : null}
        </div>
      </ManageCard>

      <ManageCard
        icon={UserCheck}
        tone="indigo"
        title="Give them to another rep"
        open={manageOpen === 'reassign'}
        dimmed={!!manageOpen && manageOpen !== 'reassign'}
        onToggle={() => toggleManage('reassign')}
        blurb="They move to that rep's board with every step."
        footer={
          <div>
            <button
              type="button"
              disabled={(notMine && !isManager) || !toRepId || reassignRun.isLoading || live}
              title={notMine && !isManager ? VIEW_ONLY_TITLE : undefined}
              onClick={doReassign}
              className="w-full whitespace-nowrap rounded-xl bg-slate-900 py-2.5 text-sm font-semibold text-white outline-none transition-[background-color] hover:bg-slate-700 focus:outline-none disabled:opacity-40 dark:!bg-slate-100 dark:!text-slate-900 dark:hover:!bg-white"
            >
              Reassign
            </button>
            {live ? (
              <p className="mb-0 mt-2 text-xs text-slate-500 dark:text-slate-400">
                Hang up first, then hand them on.
              </p>
            ) : null}
          </div>
        }
      >
        <div className="[&>*:last-child]:w-full">
          <ManageLabel>Move them to</ManageLabel>
          <Picker
            value={toRepId}
            placeholder="Pick a rep"
            options={repOptions}
            onPick={setToRepId}
            disabled={notMine && !isManager}
          />
        </div>
      </ManageCard>
    </div>
  );

  return createPortal(
    // A normal desktop size (Nic 2026-09-30): on a big monitor the Workspace
    // sits centered, capped at 1480 x 960 (WorkspaceFrame).
    <WorkspaceFrame>
        {/* ---- Top bar: who, and the call ---- */}
        {/* Who they are on the left (name, where they are, the key facts),
            the call and close on the right (Nic 2026-10-08 overhaul).
            Below lg the call controls take their own row and close stays
            top right, or the name block squeezed to nothing on a phone. */}
        <header
          aria-label="Workspace"
          className="flex flex-wrap items-start gap-x-[16px] gap-y-[12px] border-b border-slate-200/80 bg-white px-[20px] py-[12px] dark:border-white/10 dark:!bg-surface-elevated lg:items-center lg:py-[10px] md:rounded-t-3xl"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-bold text-white dark:!bg-slate-100 dark:!text-slate-900">
            {initialsOf(lead.leadFirstName, lead.leadLastName)}
          </span>
          <div className="order-[1] min-w-0 flex-1">
            <div className="flex min-w-0 flex-wrap items-center gap-x-[12px] gap-y-[2px]">
              <h2 className="mb-0 truncate text-base font-bold text-slate-800 dark:text-slate-100">
                {name || 'Lead'}
              </h2>
              <JourneyCrumb index={journey} />
            </div>
            <div className="mt-[4px] flex min-w-0 flex-wrap items-center gap-[6px]">
              {src?.detail || src?.label ? (
                <HeaderChip>
                  {[src.detail, src.label]
                    .filter((v, i, a) => v && a.indexOf(v) === i)
                    .join(' · ')}
                </HeaderChip>
              ) : null}
              {/* Info only (Nic 2026-09-30): confirmed in the signup's
                  Confirm their facts step, never picked here. */}
              <HeaderChip label="VA.gov" muted={!vaGovAnswerOf(lead)}>
                {VA_GOV_LABEL[vaGovAnswerOf(lead)] || 'Not asked yet'}
              </HeaderChip>
              {run?.ownerName ? (
                <HeaderChip label="Rep">{run.ownerName}</HeaderChip>
              ) : null}
            </div>
          </div>

          {/* Below lg the call row spans the header and its buttons share the
              width evenly, edge to edge with the avatar above. */}
          <div className="order-[3] flex basis-full flex-wrap items-center gap-2 max-lg:[&>span]:w-full lg:order-[2] lg:ml-auto lg:basis-auto">
            {call.consult ? (
              <span className="inline-flex items-center gap-2 rounded-full bg-amber-50 py-1 pl-3 pr-1 text-xs font-semibold text-amber-800 ring-1 ring-amber-200 dark:!bg-amber-500/15 dark:text-amber-200 dark:ring-amber-500/30">
                Talking to {call.consult.target.name}.{' '}
                {lead.leadFirstName || 'The veteran'} is on hold.
                <button
                  type="button"
                  disabled={busy}
                  onClick={actions.completeWarmTransfer}
                  className="rounded-full bg-emerald-600 px-3 py-1.5 text-white hover:bg-emerald-500 disabled:opacity-50 whitespace-nowrap"
                >
                  Complete transfer
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={actions.cancelWarmTransfer}
                  className="rounded-full bg-white px-3 py-1.5 text-amber-800 hover:bg-amber-100 dark:!bg-white/10 dark:text-amber-100 whitespace-nowrap"
                >
                  Cancel
                </button>
              </span>
            ) : live ? (
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 py-1 pl-3.5 pr-1 text-sm font-semibold text-emerald-800 ring-1 ring-emerald-200 dark:!bg-emerald-500/15 dark:text-emerald-200 dark:ring-emerald-500/30">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-[0.75]" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
                </span>
                On the call
                <span className="tabular-nums text-emerald-700 dark:text-emerald-300">
                  {mmss(elapsedMs)}
                </span>
                <button
                  type="button"
                  onClick={actions.toggleMute}
                  title={muted ? 'Unmute' : 'Mute'}
                  className={cn(
                    'ml-1 flex h-8 w-8 items-center justify-center rounded-full outline-none transition-[background-color] focus:outline-none whitespace-nowrap',
                    muted
                      ? 'bg-amber-500 text-white'
                      : 'bg-white text-emerald-700 hover:bg-emerald-100 dark:!bg-white/10 dark:text-emerald-200',
                  )}
                >
                  {muted ? <MicOff size={15} /> : <Mic size={15} />}
                </button>
                {/* No transfer here (Nic 2026-10-25): the hot seat lives only on
                    the Signed up screen, pick then Hot Transfer. */}
                <button
                  type="button"
                  onClick={actions.hangup}
                  title="Hang up"
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-red-600 text-white outline-none hover:bg-red-500 focus:outline-none whitespace-nowrap"
                >
                  <PhoneOff size={15} />
                </button>
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 max-lg:[&>*]:flex-1 max-lg:[&>*]:justify-center">
                {/* A call that didn't go through says so, quietly. */}
                {call.failed ? (
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[13px] font-semibold text-red-600 dark:text-red-300">
                    <span className="h-2 w-2 rounded-full bg-red-500" /> Didn&apos;t connect
                  </span>
                ) : null}
                {!call.dialing ? (
                  <TextButton
                    onClick={() => setTexting(true)}
                    disabled={notMine}
                    title={greyTitle}
                  />
                ) : null}
                {/* Dropped, forgot something, voicemail: call right back
                    without leaving the workspace (Nic 2026-09-24). */}
                {!call.dialing && (notMine || lead.leadPhone) ? (
                  <CallButton
                    name={name || 'them'}
                    phone={lead.leadPhone}
                    disabled={notMine}
                    title={greyTitle}
                    open={confirmCall}
                    onOpenChange={setConfirmCall}
                    onCall={
                      precall && !call.failed
                        ? actions.placeCall
                        : actions.redial
                    }
                  />
                ) : null}
              </span>
            )}
          </div>

          <div className="order-[2] flex items-center gap-1 self-start lg:order-[3] lg:self-center">
            {live ? (
              <button
                type="button"
                onClick={actions.minimize}
                title="Minimize. The call keeps going"
                className="rounded-lg p-2 text-slate-400 outline-none hover:bg-slate-100 hover:text-slate-600 focus:outline-none dark:hover:!bg-white/10"
              >
                <Minus size={18} />
              </button>
            ) : (
              // The X is the way out once the call is not live (Nic
              // 2026-09-30: "Not now" is gone). Never locked (Nic 2026-10-10):
              // a dropped call must not force a fake follow-up or a Not
              // interested. Contact made already put them in Book or sign up,
              // whose clock and Nurture backstop keep them from getting lost.
              <button
                type="button"
                onClick={actions.closeCenter}
                title="Close"
                className="rounded-lg p-2 text-slate-400 outline-none hover:bg-slate-100 hover:text-slate-600 focus:outline-none dark:hover:!bg-white/10"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </header>

        {/* ---- Two zones (Nic 2026-10-08 overhaul, was three panels) ----
            The rail (about them, notes, history) and the work (one row of
            tabs: Objectives, If they ask, Sign up). From lg each fills the
            height and scrolls on its own; below lg they stack and the
            workspace scrolls as one page. */}
        <div className="os-scroll grid min-h-0 flex-1 auto-rows-max grid-cols-[minmax(0,1fr)] content-start gap-[12px] overflow-y-auto p-[12px] lg:auto-rows-auto lg:grid-cols-[19rem_minmax(0,1fr)] lg:content-stretch lg:overflow-hidden xl:grid-cols-[21rem_minmax(0,1fr)] xl:gap-[16px] xl:p-[16px]">
          <PersonPanel
            lead={lead}
            run={run}
            readOnly={notMine}
            emailLocked={
              !!signup.done?.register ||
              ['account_made', 'agreement_out'].includes(run?.pipelineStage)
            }
            armedChannel={mode === 'signup' ? armedChannel : null}
            onEmailSaved={(leadEmail) => {
              actions.patchLead?.({ leadEmail });
              queryClient.invalidateQueries(['onboarding-board']);
            }}
          />

          {/* Folder tabs, all in (Nic 2026-10-08): the tabs sit on the
              Workspace background above the card; the card IS the open
              tab's page. Each tab has its own color (TAB_TONE); the open one
              is white, outlined in its color with the page under it, and
              covers the page's top border so the two read as one piece.
              Tabs: Objectives, If they ask, Manage, Sign up. Tabs start past
              the corner radius. */}
          <section className="flex min-h-0 flex-col">
            <FolderTabs
              tabs={workTabs}
              active={activeTab}
              onSelect={(key) => {
                if (key === 'signup') {
                  actions.setWorkspaceMode('signup');
                } else {
                  setGuideSub(key);
                  actions.setWorkspaceMode('guide');
                }
              }}
            />
            {/* The page: a 2px outline in the open tab's color (Nic
                2026-10-08), scrolling in an inset box. */}
            <TabPage
              tone={workTabs.find((t) => t.key === activeTab)?.tone || 'sky'}
            >
                {/* Their follow-up on every tab (Nic 2026-10-26, like
                    Onboarding's first meeting strip): when, phone or video,
                    and Reschedule / Cancel right there. Manage has its own. */}
                {detectedBooking && activeTab !== 'manage' ? (
                  <div className="mb-[10px] rounded-xl bg-slate-50 px-[12px] py-[8px] text-[13px] ring-1 ring-slate-200/70 dark:!bg-white/[0.04] dark:ring-white/10">
                    <MeetingActions
                      meeting={detectedBooking}
                      mount="board"
                      leadId={lead.leadId}
                      canAct={!notMine}
                      onReschedule={setMoving}
                      label={
                        <span className="inline-flex items-center gap-[6px] font-semibold text-slate-800 dark:text-slate-100">
                          <span className="h-[7px] w-[7px] rounded-full bg-sky-500" />
                          Follow-up
                        </span>
                      }
                    />
                  </div>
                ) : null}
                {mode === 'signup' ? (
                  <SignupBody
                    leadId={lead.leadId}
                    leadName={name}
                    ownerUserId={run ? run.ownerUserId || null : undefined}
                    layout="top"
                    onArmChange={setArmedChannel}
                    onDone={() => actions.setWorkspaceMode('guide')}
                    doneExtra={hotSeatCard}
                    // Still on the call: Done only hid the hot seat (Nic
                    // 2026-10-25), so it shows once the call is over.
                    hideDone={!!call.callLive}
                  />
                ) : activeTab === 'manage' ? (
                  managePage
                ) : activeTab === 'transcripts' ? (
                  <LeadTranscriptsCard leadId={lead.leadId} endpointBase="/onboarding-board/leads" bare />
                ) : activeTab === 'notes' ? null : (
                  <GuidePanel
                    guide={boardContext?.callGuide}
                    firstName={lead.leadFirstName}
                    sub={guideSub}
                  />
                )}
                {/* Always mounted, shown only on its tab. */}
                <div hidden={mode === 'signup' || activeTab !== 'notes'}>
                  <NotesTab
                    lead={lead}
                    readOnly={notMine}
                    onSaved={(patch) => actions.patchLead?.(patch)}
                  />
                </div>
            </TabPage>
          </section>
        </div>

        {/* ---- The next-step bar ---- */}
        <footer className="border-t border-slate-200/80 bg-white px-[20px] py-[10px] dark:border-white/10 dark:!bg-surface-elevated md:rounded-b-3xl">
          {notMine ? (
            <NotYoursNote ownerName={run.ownerName} viewOnly={viewOnly} />
          ) : signup.signed ? (
            <div className="flex flex-wrap items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500 text-white">
                <Check size={18} strokeWidth={3} />
              </span>
              <div className="mr-auto">
                <div className="text-sm font-bold text-slate-800 dark:text-slate-100">
                  {lead.leadFirstName || 'They'} signed up. Nice work.
                </div>
                <div className="text-xs text-slate-400">
                  They are a client now and off your board.
                  {call.hotSeatDone
                    ? ` ${call.hotSeatDone.toName} has them now.`
                    : (call.callLive || liveCall) && !vaGovReady
                      ? " VA.gov comes first, so no hot transfer. Their specialist reaches out once they've set it up."
                      : call.callLive
                        ? ' Hand them over from Signed up (Hot seat transfer).'
                        : liveCall
                          ? " You're on the call on your phone. Hand them over from Signed up (Hot seat transfer)."
                          : signedOnCall
                            ? ''
                            : ' No call going, so no hot seat. Onboarding assigns them a specialist, who reaches out.'}
                </div>
              </div>
            </div>
          ) : parked ? (
            <div className="flex flex-wrap items-center gap-3">
              <div className="mr-auto text-sm font-semibold text-slate-700 dark:text-slate-200">
                {parked === 'nurture'
                  ? "Moved to Nurture. We'll alert you if they reach back out."
                  : 'Lead dropped.'}{' '}
                <span className="font-normal text-slate-400">
                  Hang up when you are ready.
                </span>
              </div>
            </div>
          ) : (
            // "Client not ready?" (Nic 2026-09-30): signing up lives in the
            // Sign up tab; this bar is only for a veteran who is not signing
            // today. No booking link: the rep books the follow-up with them
            // on the line (Jorge's First Touch doc: never leave it to the
            // veteran to call back). Wide: text left, buttons right.
            <div className="flex flex-col gap-[10px] lg:flex-row lg:items-center">
              <div className="flex min-w-0 items-center gap-[12px] lg:mr-auto">
                {/* The bar's state at a glance (Nic 2026-10-08): a green
                    check only when the line says the next step is set (the
                    call ended with one); "Client not ready?" and "What
                    happens next?" get an amber question mark. */}
                {(() => {
                  const settled = !live && !precall && nextStepSet;
                  return (
                    <span
                      className={cn(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                        settled ? SECTION_TONE.emerald : SECTION_TONE.amber,
                      )}
                    >
                      {settled ? (
                        <Check size={17} strokeWidth={2.5} />
                      ) : (
                        <CircleQuestionMark size={18} strokeWidth={2.25} />
                      )}
                    </span>
                  );
                })()}
                <div className="min-w-0">
                <div className="text-sm font-bold text-slate-800 dark:text-slate-100">
                  {!live && !precall && nextStepSet
                    ? 'Call ended. Next step is set.'
                    : !live && !precall && (requireOutcome || mustBookReview)
                      ? 'Call ended. What happens next?'
                      : !live && !precall
                        ? 'Call ended. Client not ready?'
                        : 'Client not ready?'}
                </div>
                <div
                  className={cn(
                    'text-xs',
                    !live && (requireOutcome || mustBookReview)
                      ? 'font-semibold text-amber-700 dark:text-amber-300'
                      : 'text-slate-400',
                  )}
                >
                  {detectedBooking || reviewBooked
                    ? 'Their follow-up is booked.'
                    : mustBookReview
                      ? `${reviewReason}. Book their follow-up within the next 2 days (tomorrow or the day after, 2026-10-27) before you close. ${NO_BOOK_RULE}`
                      : signupStarted
                        ? `Their signup is at step ${signupStepNumber(signup.current)} of 4.`
                        : `Book their follow-up within the next 2 days (tomorrow or the day after, 2026-10-27) while they are on the line. ${NO_BOOK_RULE}`}
                </div>
                </div>
              </div>

              {/* Book follow-up, Nurture and Drop live in the Manage tab
                  (Nic 2026-10-14); this bar keeps the prompt and opens it,
                  so the button hides while Manage is already open (Nic
                  2026-10-15). */}
              {mode !== 'signup' && activeTab === 'manage' ? null : (
              <div className="lg:shrink-0">
                <button
                  type="button"
                  onClick={openManage}
                  className={cn(quietBtn, 'w-full justify-center lg:w-auto')}
                >
                  <Settings2 size={15} /> Book, Nurture or Drop
                  <ChevronRight size={14} className="text-slate-400" />
                </button>
              </div>
              )}

              {/* No Go there / Done (Nic 2026-09-30): the X closes. Only an
                  unbooked follow-up keeps a button here. */}
              {!live && mustBookReview ? (
                <div className="flex flex-wrap justify-end gap-2 lg:shrink-0">
                  <button
                    type="button"
                    disabled={setOutcome.isLoading}
                    onClick={() => nurtureWith(COULDNT_BOOK)}
                    title="Moves them to Nurture. We'll alert you if they ever reach back out."
                    className={cn(
                      barBtn,
                      'bg-violet-50 text-violet-700 hover:bg-violet-100 dark:!bg-violet-500/15 dark:text-violet-200 dark:hover:!bg-violet-500/25',
                    )}
                  >
                    <Moon size={15} /> Couldn't book: Nurture
                  </button>
                  <button
                    type="button"
                    onClick={() => setBookPicker(true)}
                    className={cn(barBtn, 'bg-sky-600 text-white hover:bg-sky-500')}
                  >
                    <Calendar size={15} /> Book the follow-up to finish
                  </button>
                </div>
              ) : null}
            </div>
          )}
        </footer>

      <NudgeComposer
        open={texting}
        blank
        lead={lead}
        onClose={() => setTexting(false)}
      />

      {moving ? (
        <SlotPickerOverlay
          mount="callcenter"
          leadId={lead.leadId}
          leadName={name}
          reschedule={{ bookingId: moving.calendlyBookingId, kind: moving.kind, timezone: moving.timezone }}
          onBooked={() => queryClient.invalidateQueries(['onboarding-board'])}
          onClose={() => setMoving(null)}
        />
      ) : null}
      {bookPicker ? (
        <SlotPickerOverlay
          mount="callcenter"
          leadId={lead.leadId}
          leadName={name}
          within={reviewBooked ? null : bookWindow}
          onBooked={() => {
            if (reviewPending) setReviewBooked(true);
          }}
          onClose={() => setBookPicker(false)}
        />
      ) : null}
    </WorkspaceFrame>,
    document.body,
  );
};

export default CallWorkspace;
